import React from 'react';
import ReactDOM from 'react-dom';
import { safewordAPI } from '../../services/api';
import { getAppLockService } from '../../services/AppLockService';
import { unwrapResponse } from '../../utils/unwrapResponse';
import { CRISIS_LINES } from '../../components/safety/ResourcesSection';
import {
  NO_HIERARCHY_HEADLINE,
  NO_HIERARCHY_TOLD_NO_ONE,
  NO_HIERARCHY_PLATFORM_NOTE,
  HIERARCHY_UNCONFIRMED,
  reportNoHierarchySafeword,
} from '../../utils/noHierarchySafeword';

interface SafewordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Emergency safeword modal rendered as a React portal on document.body.
 *
 * CRITICAL SAFETY INVARIANT:
 * - This component renders OVER the AppLockOverlay, never replacing it.
 * - onClose only dismisses this modal — the lock screen remains active.
 * - Users can trigger their safeword while the app is locked.
 */
const SafewordModal: React.FC<SafewordModalProps> = ({ isOpen, onClose }) => {
  const [triggering, setTriggering] = React.useState(false);
  const [triggered, setTriggered] = React.useState<'soft' | 'hard' | null>(null);
  const [notifiedSomeone, setNotifiedSomeone] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  // 0zzz-uhy: the user belongs to no hierarchy, so nobody in the app can be told. Honest dead end + crisis lines.
  const [noHierarchy, setNoHierarchy] = React.useState(false);

  const handleTrigger = async (level: 'soft_limit' | 'hard_limit') => {
    setTriggering(true);
    setError(null);
    try {
      const response =
        level === 'soft_limit'
          ? await safewordAPI.triggerSoftLimit({})
          : await safewordAPI.triggerHardLimit({});
      // SECURITY (2026-08-09 Safeword red-team review, CRITICAL): don't
      // unconditionally claim someone was notified — dominantCount/
      // notifiedCount are the backend's real delivery outcome
      // (safewordService.ts's triggerSafeword). A 2xx response only means the
      // trigger was logged, not that anyone was actually reached.
      //
      // isDuplicate (fixed same day after a review found the first version of
      // the backend's idempotency guard was itself a regression): a duplicate
      // trigger always reports dominantCount:0/notifiedCount:0 because THIS
      // call skipped notifying (the original call already did it) — treating
      // that as "nobody was notified" would be a false negative, since the
      // original trigger may well have succeeded. Duplicates get the normal
      // "notified" message instead of the warning.
      const result = unwrapResponse<{ dominantCount?: number; notifiedCount?: number; isDuplicate?: boolean; cooldownSuppressed?: boolean; log?: { id: number } }>(response.data);
      const dominantCount = result?.dominantCount ?? 0;
      const notifiedCount = result?.notifiedCount ?? 0;
      // 0zzz-uhv F2: a cooldown-suppressed trigger is 0/0 for the same reason a duplicate is (this
      // call skipped notifying on purpose; the original trigger did), so treat it the same way.
      setNotifiedSomeone(result?.isDuplicate === true || result?.cooldownSuppressed === true || (dominantCount > 0 && notifiedCount >= dominantCount));
      setTriggered(level === 'soft_limit' ? 'soft' : 'hard');
      // Force fresh safeword status check on next foreground event — prevents
      // the 60s cache from incorrectly allowing a lock during an active safeword.
      getAppLockService().invalidateSafewordCache();

      // SAFETY (bug-registry 0zzz-au follow-up, HIGH): unlike the normal SafewordPage trigger
      // path (useSafeword.ts's triggerMutation), this emergency/locked-app trigger deliberately
      // does NOT navigate to the aftercare check-in screen. Silently changing the underlying
      // route while the app is locked would contradict this modal's own "Close (app remains
      // locked)" design intent and AppLockOverlay's stated invariant that the lock screen is
      // never navigated away from while active — and a user who just hit a hard limit may not
      // even remember triggering it by the time they eventually unlock, so surfacing it via the
      // same resumable "Check-in waiting for you" card (AftercareResumeCard) that already exists
      // for a backgrounded/closed-app check-in is the more consistent UX, not a lesser one.
      //
      // But that resumable card — and SafewordHistory's status row — both key off
      // safeword_log.aftercare_shown, which (per safewordService.getAftercareData's own comment)
      // is ONLY ever set by that endpoint, normally called when the check-in screen mounts. This
      // trigger path never mounts that screen, so without the call below the flag would never get
      // set and the user would have NO UI path back to their check-in, ever — even after
      // unlocking. Fire the same GET the check-in screen itself would call, purely for that side
      // effect (the response body isn't used). Best-effort and fire-and-forget: the trigger
      // itself already succeeded and was already logged, so a failure here must not surface as a
      // trigger failure — it only means this specific attempt to pre-set the flag didn't land;
      // the flag would still get set the next time the user actually opens the check-in screen.
      if (level === 'hard_limit' && result?.log?.id) {
        safewordAPI.getAftercareData(result.log.id).catch(() => {
          // Best-effort only — see comment above.
        });
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; code?: string } } };
      if (e?.response?.status === 404 && e.response.data?.code === 'NO_HIERARCHY') {
        setNoHierarchy(true);
        // An explicit press, so the alert stays. A 409 means the server found a hierarchy for this user (a race or a
        // failed load): withdraw the claim and show the retry state with the trigger buttons back.
        void reportNoHierarchySafeword().then((outcome) => {
          if (outcome === 'member') {
            setNoHierarchy(false);
            setError(HIERARCHY_UNCONFIRMED);
          }
        });
        return;
      }
      setError(e?.response?.data?.error || 'Failed to trigger safeword. Please try again.');
    } finally {
      setTriggering(false);
    }
  };

  if (!isOpen) return null;

  // DESIGN-SYSTEM EXEMPTION (Spec 236 Wave 2, 2026-06-09): intentionally not the Modal
  // primitive. This dialog must layer ABOVE the z-[10001] lock screen (primitives cap at
  // z-[300]) while preserving the safety invariant that dismissing it never unlocks the
  // app. Counted in the adhoc_overlay ratchet baseline as a sanctioned exception.
  const modal = (
    <div
      className="fixed inset-0 z-[10002] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Emergency safeword"
    >
      {/* Backdrop — does NOT close the lock screen, only this modal */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />

      <div className="relative z-[10003] w-full max-w-sm bg-[var(--theme-card-bg)] border border-white/10 rounded-2xl p-6 shadow-2xl">
        <h2 className="text-white text-lg font-semibold mb-1">Emergency / Safeword</h2>
        <p className="text-white/60 text-sm mb-5">
          You can trigger your safeword even while the app is locked.
        </p>

        {noHierarchy || triggered ? (
          <div className="space-y-3" data-testid={noHierarchy ? 'safeword-modal-no-hierarchy' : undefined}>
            {noHierarchy ? (
              <>
                <div className="rounded-xl bg-amber-900/40 border border-amber-500/30 px-4 py-3 text-amber-200 text-sm space-y-2">
                  <p className="font-semibold m-0">{NO_HIERARCHY_HEADLINE}</p>
                  <p className="m-0">{NO_HIERARCHY_TOLD_NO_ONE}</p>
                  <p className="m-0 text-amber-200/80">{NO_HIERARCHY_PLATFORM_NOTE}</p>
                </div>
                <ul className="list-none m-0 p-0">
                  {CRISIS_LINES.map((line) => (
                    <li key={line.name} className="py-2 border-b border-white/10 text-white text-sm">
                      <span className="font-semibold">{line.name}</span>
                      <span className="block text-white/70">{line.contact}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="rounded-xl bg-green-900/40 border border-green-500/30 px-4 py-3 text-green-300 text-sm">
                {triggered === 'soft'
                  ? notifiedSomeone
                    ? 'Soft limit triggered. Your partner has been notified.'
                    : 'Soft limit triggered and logged. We could not confirm your partner was notified — please reach out directly if you need support.'
                  : notifiedSomeone
                    ? 'Hard limit triggered. All tasks have been paused.'
                    : 'Hard limit triggered. All tasks have been paused. We could not confirm your partner was notified — please reach out directly if you need support.'}
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl bg-white/10 text-white font-medium text-sm hover:bg-white/20 transition-colors"
            >
              Close (app remains locked)
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {error && (
              <div className="rounded-xl bg-red-900/40 border border-red-500/30 px-4 py-3 text-red-300 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={() => handleTrigger('soft_limit')}
              disabled={triggering}
              className="w-full py-3 rounded-xl bg-amber-600/80 text-white font-medium text-sm hover:bg-amber-600 active:bg-amber-700 transition-colors disabled:opacity-50"
            >
              {triggering ? 'Triggering...' : 'Soft Limit — Slow down'}
            </button>

            <button
              onClick={() => handleTrigger('hard_limit')}
              disabled={triggering}
              className="w-full py-3 rounded-xl bg-red-700/80 text-white font-semibold text-sm hover:bg-red-700 active:bg-red-800 transition-colors disabled:opacity-50"
            >
              {triggering ? 'Triggering...' : 'Hard Limit — Full stop'}
            </button>

            <button
              onClick={onClose}
              className="w-full py-2 text-white/40 text-sm hover:text-white/60 transition-colors"
            >
              Cancel — return to lock screen
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default SafewordModal;
